import PageHeader from '@/components/PageHeader';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { styled } from 'styled-components';

import { ReactComponent as ExtLinkIcon } from '@/assets/icons/ext-link.icon.svg';
import { ReactComponent as FileIcon } from '@/assets/icons/attachment.icon.svg';
import PageLoader from '@/loaders/PageLoader';
import { toast } from 'react-hot-toast';
import { fetchBill } from '@/api/billsApi';
import { numberWithCommas } from '@/utils/helpers';
import { ReactComponent as BackIcon } from '@/assets/icons/back.icon.svg';
import { formatInTimeZone } from 'date-fns-tz';
import { useEffect, useState } from 'react';

function BillsDetails() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { id } = useParams();

  const bill = useQuery({
    queryKey: ['bills', { id }],
    queryFn: fetchBill,
    onSuccess: (data) => {}
  });

  const [currentDetailPage, setCurrentDetailPage] = useState(1);

  const params = new URLSearchParams(search);
  const currentPage = params.get('page');

  const handleReturn = () => {
    window.history.back();
  };

  useEffect(() => {
    if (currentPage) {
      setCurrentDetailPage(parseInt(currentPage));
    }
  }, [currentPage]);

  if (bill.isFetching) {
    return <PageLoader />;
  }

  return (
    <>
      <PageHeader title="Bills details" subtitle="View and manage a Bill" />
      <BillsDetailsView>
        <div className="return" onClick={handleReturn}>
          <BackIcon />
        </div>
        <DataView>
          <FormData>
            <div className="item">
              <div className="label-view">
                <h3>Bill Status</h3>
                <Status status={bill?.data?.data?.status}>{bill?.data?.data?.status}</Status>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Bill Type</h3>
                <p>{bill?.data?.data?.type || '--'}</p>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Vendor Name</h3>
                <p>{bill?.data?.data?.vendor?.companyName || '--'}</p>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Bill No:</h3>
                <p>{bill?.data?.data?.billOrInvoiceNumber || '--'}</p>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Bill Id</h3>
                <p>{bill?.data?.data?.id || '--'}</p>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Frequency</h3>
                <p>{bill?.data?.data?.frequency || '--'}</p>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Recurring Start Date</h3>
                <p>
                  {bill?.data?.data?.recurringStartDate
                    ? formatInTimeZone(
                        new Date(bill?.data?.data?.recurringStartDate),
                        'UTC',
                        'do MMM, yyyy'
                      )
                    : '--'}
                </p>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Recurring End Date</h3>
                <p>
                  {bill?.data?.data?.recurringEndDate
                    ? formatInTimeZone(
                        new Date(bill?.data?.data?.recurringEndDate),
                        'UTC',
                        'do MMM, yyyy'
                      )
                    : '--'}
                  {bill?.data?.data?.recurringEndDate !== null}
                </p>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Next Recurring Date</h3>
                <p>
                  {bill?.data?.data?.nextrecurringAt
                    ? formatInTimeZone(
                        new Date(bill?.data?.data?.nextrecurringAt),
                        'UTC',
                        'do MMM, yyyy'
                      )
                    : '--'}
                </p>
              </div>
            </div>

            <div className="item">
              <div className="label-view">
                <h3>Subject</h3>
                <p>{bill?.data?.data?.subject || '--'}</p>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Amount</h3>
                <p>₦{numberWithCommas(bill?.data?.data?.amount || '00.00')}</p>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Date Created</h3>
                <p>
                  {bill?.data?.data?.createdAt
                    ? formatInTimeZone(
                        new Date(bill?.data?.data?.createdAt),
                        'UTC',
                        'do MMM, yyyy, h:mm aa'
                      )
                    : '--'}
                </p>
              </div>
            </div>
            <div className="item">
              <div className="label-view">
                <h3>Date Updated</h3>
                <p>
                  {bill?.data?.data?.updatedAt
                    ? formatInTimeZone(
                        new Date(bill?.data?.data?.updatedAt),
                        'UTC',
                        'do MMM, yyyy, h:mm aa'
                      )
                    : '--'}
                </p>
              </div>
            </div>
          </FormData>

          <ColumnView>
            <FormData>
              <div className="item">
                <div className="label-view">
                  <h3>User Id</h3>
                  <p>{bill?.data?.data?.vendor?.connectedUserId || '--'}</p>
                </div>
                <div className="copy-view">
                  <button
                    onClick={() => {
                      navigate(`/users/${bill?.data?.data?.vendor?.connectedUserId}`);
                    }}>
                    View <ExtLinkIcon />
                  </button>
                </div>
              </div>
              <div className="item">
                <div className="label-view">
                  <h3>Business Name</h3>
                  <p>{bill?.data?.data?.user?.companies?.[0]?.name || '--'}</p>
                </div>
              </div>
              <div className="item">
                <div className="label-view">
                  <h3>Description</h3>
                  <p>{bill?.data?.data?.description || '--'}</p>
                </div>
              </div>
            </FormData>

            {bill?.data?.data?.fileUrl && (
              <FileView>
                <h3>Attached files</h3>
                <p>Files and assets that have been attached to this submission</p>
                <FileItem>
                  <div className="icon">
                    <FileIcon />
                  </div>
                  <div className="label-view">
                    <h3>Bill Attachment</h3>
                    <p>
                      {bill?.data?.data?.fileUrl.substring(
                        bill?.data?.data?.fileUrl.lastIndexOf('/') + 1
                      )}
                    </p>
                  </div>
                  <div className="view">
                    <a href={bill?.data?.data?.fileUrl} target="_blank" rel="noopener noreferrer">
                      View <ExtLinkIcon />
                    </a>
                  </div>
                </FileItem>
              </FileView>
            )}
          </ColumnView>
        </DataView>
      </BillsDetailsView>
    </>
  );
}

export default BillsDetails;

const Status = styled.p`
  color: ${({ status }) => {
    switch (status) {
      case 'sent':
        return '#16A34A';
      case 'paid':
        return '#16A34A';
      case 'partially-paid':
        return '#F4B740';
      case 'pending':
        return '#F4B740';
      case 'draft':
        return '#F4B740';
      case 'pending':
        return '#F4B740';
      case 'ready':
        return '#F4B740';
      case 'unsent':
        return '#B91C1C';

      default:
    }
  }};
  text-transform: capitalize;
`;

const BillsDetailsView = styled.div`
  width: 82%;
  padding: 30px;
  background-color: ${({ theme }) => theme.colors.white};
  overflow-y: scroll;
  height: 100%;
  padding-top: 120px;
  overflow-x: hidden;
  margin-left: 18%;

  .return {
    width: 30px;
    margin-bottom: 20px;
    cursor: pointer;
  }

  .progress-title {
    width: 100%;
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;

    h3 {
      font-size: 0.875rem;
      font-weight: 500;
      color: ${({ theme }) => theme?.colors?.secondary};
    }

    p {
      font-size: 0.875rem;
      font-weight: 500;
      color: ${({ theme }) => theme?.colors?.secondary};
    }
  }
`;

const DataView = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors?.white};
  display: flex;
  justify-content: space-between;
  gap: 1rem;
`;

const FormData = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors?.white};
  border-radius: 6px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors?.line};

  .item {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors?.line};

    .label-view {
      width: 70%;
      display: flex;
      justify-content: center;
      flex-direction: column;
      color: ${({ theme }) => theme?.colors?.secondary};

      h3 {
        font-size: 0.875rem;
        font-weight: 500;
        color: ${({ theme }) => theme?.colors?.secondary};
        margin-bottom: 8px;
      }

      p {
        font-size: 0.875rem;
        font-weight: 600;
      }

      .capitalize {
        text-transform: capitalize;
      }
    }

    .copy-view {
      width: 120px;
      display: flex;
      align-items: center;

      button,
      a {
        width: 100%;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        color: ${({ theme }) => theme?.colors?.info};
        font-size: 0.875rem;

        svg {
          margin-left: 5px;
          width: 16px;
          height: 16px;

          path {
            stroke: ${({ theme }) => theme?.colors?.info};
          }
        }
      }
    }
  }

  .last {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const ColumnView = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FileView = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors?.white};
  padding-left: 20px;

  h3 {
    font-size: 1.2rem;
    font-weight: 600;
    color: ${({ theme }) => theme?.colors?.secondary};
    margin-bottom: 8px;
  }

  .verification {
    margin-top: 40px;
  }

  p {
    font-size: 0.875rem;
    font-weight: 500;
    color: ${({ theme }) => theme?.colors?.secondary};
  }

  .sub-text {
    margin-bottom: 20px;
  }
`;

const FileItem = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background-color: #f3f2f2;
  border-radius: 6px;
  margin-top: 20px;

  .icon {
    width: 50px;
    height: 50px;
    border-radius: 6px;
    background-color: ${({ theme }) => theme.colors?.white};
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .label-view {
    width: calc(100% - 190px);

    h3 {
      font-size: 0.875rem;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.secondary};
    }

    p {
      font-size: 0.875rem;
      font-weight: 500;
      color: ${({ theme }) => theme?.colors?.secondary};
    }
  }

  .view {
    width: 120px;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    a {
      color: ${({ theme }) => theme?.colors?.info};
      font-size: 0.875rem;
      display: flex;
      align-items: center;

      svg {
        margin-left: 5px;
        width: 16px;
        height: 16px;

        path {
          stroke: ${({ theme }) => theme?.colors?.info};
        }
      }
    }
  }
`;
